import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, TrendingDown, TrendingUp, AlertTriangle, ArrowDownToLine, Flame } from "lucide-react";

const UNITS = [
  { value: "each", label: "Each", family: "count" },
  { value: "dozen", label: "Dozen", family: "count" },
  { value: "case", label: "Case", family: "count" },
  { value: "oz", label: "Ounce (oz)", family: "weight" },
  { value: "lb", label: "Pound (lb)", family: "weight" },
  { value: "g", label: "Gram (g)", family: "weight" },
  { value: "kg", label: "Kilogram (kg)", family: "weight" },
  { value: "ml", label: "Milliliter (ml)", family: "volume" },
  { value: "l", label: "Liter (l)", family: "volume" },
  { value: "fl_oz", label: "Fluid Oz", family: "volume" },
  { value: "cup", label: "Cup", family: "volume" },
  { value: "gallon", label: "Gallon", family: "volume" },
];

const CATEGORIES = ["Dough", "Cheese", "Meat", "Vegetables", "Sauce", "Dairy", "Dry Goods", "Beverages", "Packaging", "Other"];

export default function InventoryPage() {
  const [tab, setTab] = useState("ingredients");
  const [ingredients, setIngredients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Ingredient dialog
  const [showIngDialog, setShowIngDialog] = useState(false);
  const [editingIng, setEditingIng] = useState(null);
  const [ingForm, setIngForm] = useState({
    name: "", stock_unit: "each", cost_per_unit: "", current_stock: "", reorder_level: "", vendor_id: "", category: "", case_size: "1", is_active: true,
  });

  // Receive/Spoil dialog
  const [showTxDialog, setShowTxDialog] = useState(false);
  const [txType, setTxType] = useState("receive");
  const [txForm, setTxForm] = useState({ ingredient_id: "", qty: "", unit: "", cost_per_unit: "", vendor_id: "", notes: "" });

  const fetchData = useCallback(async () => {
    try {
      const [iRes, vRes, tRes] = await Promise.all([
        api.get("/ingredients"),
        api.get("/vendors"),
        api.get("/inventory/transactions?limit=100"),
      ]);
      setIngredients(iRes.data);
      setVendors(vRes.data);
      setTransactions(tRes.data);
    } catch { toast.error("Failed to load inventory data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddIng = () => {
    setEditingIng(null);
    setIngForm({ name: "", stock_unit: "each", cost_per_unit: "", current_stock: "0", reorder_level: "", vendor_id: "", category: "", case_size: "1", is_active: true });
    setShowIngDialog(true);
  };

  const openEditIng = (ing) => {
    setEditingIng(ing);
    setIngForm({
      name: ing.name, stock_unit: ing.stock_unit || "each",
      cost_per_unit: String(ing.cost_per_unit || ""), current_stock: String(ing.current_stock || "0"),
      reorder_level: String(ing.reorder_level || ""), vendor_id: ing.vendor_id || "",
      category: ing.category || "", case_size: String(ing.case_size || "1"), is_active: ing.is_active !== false,
    });
    setShowIngDialog(true);
  };

  const saveIng = async () => {
    if (!ingForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = {
        ...ingForm,
        cost_per_unit: parseFloat(ingForm.cost_per_unit) || 0,
        current_stock: parseFloat(ingForm.current_stock) || 0,
        reorder_level: parseFloat(ingForm.reorder_level) || 0,
        case_size: parseFloat(ingForm.case_size) || 1,
      };
      if (editingIng) { await api.put(`/ingredients/${editingIng.ingredient_id}`, payload); toast.success("Updated"); }
      else { await api.post("/ingredients", payload); toast.success("Created"); }
      setShowIngDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteIng = async (id) => {
    if (!window.confirm("Delete this ingredient?")) return;
    try { await api.delete(`/ingredients/${id}`); toast.success("Deleted"); fetchData(); }
    catch { toast.error("Failed"); }
  };

  const openTx = (type, ingredientId = "") => {
    setTxType(type);
    setTxForm({ ingredient_id: ingredientId, qty: "", unit: "", cost_per_unit: "", vendor_id: "", notes: "" });
    setShowTxDialog(true);
  };

  const saveTx = async () => {
    if (!txForm.ingredient_id || !txForm.qty) { toast.error("Select ingredient and enter quantity"); return; }
    try {
      const endpoint = txType === "receive" ? "/inventory/receive" : "/inventory/spoil";
      const payload = {
        ingredient_id: txForm.ingredient_id,
        transaction_type: txType,
        qty: parseFloat(txForm.qty) || 0,
        unit: txForm.unit || "",
        cost_per_unit: parseFloat(txForm.cost_per_unit) || 0,
        vendor_id: txForm.vendor_id || "",
        notes: txForm.notes || "",
      };
      await api.post(endpoint, payload);
      toast.success(txType === "receive" ? "Stock received!" : "Spoilage logged!");
      setShowTxDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const getVendorName = (id) => vendors.find((v) => v.vendor_id === id)?.name || "";
  const lowStockCount = ingredients.filter((i) => i.current_stock <= i.reorder_level && i.reorder_level > 0).length;

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-6" data-testid="inventory-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="inventory-title">Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">{ingredients.length} ingredients tracked</p>
        </div>
        <div className="flex gap-2">
          {lowStockCount > 0 && (
            <Badge className="bg-red-100 text-red-700 border-red-200 gap-1"><AlertTriangle className="h-3 w-3" /> {lowStockCount} low stock</Badge>
          )}
          <Button variant="outline" onClick={() => openTx("receive")} data-testid="receive-btn">
            <ArrowDownToLine className="h-4 w-4 mr-1" /> Receive
          </Button>
          <Button variant="outline" onClick={() => openTx("spoil")} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="spoil-btn">
            <Flame className="h-4 w-4 mr-1" /> Spoil
          </Button>
          <Button onClick={openAddIng} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-ingredient-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Ingredient
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="inventory-tabs">
        {[
          { id: "ingredients", label: "Ingredients", count: ingredients.length },
          { id: "receiving", label: "Receiving Log" },
          { id: "spoilage", label: "Spoilage Log" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`tab-${t.id}`}>
            {t.label} {t.count !== undefined && <Badge variant="secondary" className="ml-1 text-[10px]">{t.count}</Badge>}
          </button>
        ))}
      </div>

      {/* Ingredients Tab */}
      {tab === "ingredients" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ingredients-list">
          <div className="grid grid-cols-[minmax(0,1fr)_80px_100px_100px_80px_100px_80px_80px] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-200">
            <div>Ingredient</div><div className="text-center">Unit</div><div className="text-right">Stock</div><div className="text-right">Reorder</div><div className="text-right">Cost</div><div>Vendor</div><div>Category</div><div className="text-right">Actions</div>
          </div>
          {ingredients.map((ing) => {
            const isLow = ing.current_stock <= ing.reorder_level && ing.reorder_level > 0;
            const isOut = ing.current_stock <= 0;
            return (
              <div key={ing.ingredient_id} className={`grid grid-cols-[minmax(0,1fr)_80px_100px_100px_80px_100px_80px_80px] gap-2 items-center px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 group ${isOut ? "bg-red-50/50" : isLow ? "bg-amber-50/50" : ""}`} data-testid={`ing-row-${ing.ingredient_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Package className={`h-4 w-4 flex-shrink-0 ${isOut ? "text-red-500" : isLow ? "text-amber-500" : "text-[var(--brand-primary)]"}`} />
                  <span className="text-sm font-medium text-slate-800 truncate">{ing.name}</span>
                  {isOut && <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200 py-0 px-1">OUT</Badge>}
                  {isLow && !isOut && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 py-0 px-1">LOW</Badge>}
                </div>
                <div className="text-center"><Badge variant="outline" className="text-xs font-mono">{ing.stock_unit}</Badge></div>
                <div className={`text-right text-sm font-mono font-semibold ${isOut ? "text-red-600" : isLow ? "text-amber-600" : "text-slate-700"}`}>{(ing.current_stock || 0).toFixed(1)}</div>
                <div className="text-right text-sm font-mono text-slate-500">{ing.reorder_level || "—"}</div>
                <div className="text-right text-sm font-mono text-slate-600">${(ing.cost_per_unit || 0).toFixed(2)}</div>
                <div className="text-sm text-slate-500 truncate">{getVendorName(ing.vendor_id) || "—"}</div>
                <div><Badge variant="secondary" className="text-[10px]">{ing.category || "—"}</Badge></div>
                <div className="text-right flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openTx("receive", ing.ingredient_id)} className="p-1 rounded hover:bg-emerald-100" title="Receive"><ArrowDownToLine className="h-3.5 w-3.5 text-emerald-600" /></button>
                  <button onClick={() => openTx("spoil", ing.ingredient_id)} className="p-1 rounded hover:bg-red-100" title="Spoil"><Flame className="h-3.5 w-3.5 text-red-500" /></button>
                  <button onClick={() => openEditIng(ing)} className="p-1 rounded hover:bg-blue-100"><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
                  <button onClick={() => deleteIng(ing.ingredient_id)} className="p-1 rounded hover:bg-red-100"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                </div>
              </div>
            );
          })}
          {ingredients.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No ingredients yet. Add your first ingredient to track inventory.</div>}
        </div>
      )}

      {/* Receiving Log Tab */}
      {tab === "receiving" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="receiving-log">
          <div className="grid grid-cols-[150px_minmax(0,1fr)_80px_80px_80px_100px_minmax(0,1fr)] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b">
            <div>Date</div><div>Ingredient</div><div className="text-right">Qty</div><div>Unit</div><div className="text-right">Cost/Unit</div><div>Vendor</div><div>Notes</div>
          </div>
          {transactions.filter((t) => t.transaction_type === "receive").map((tx) => (
            <div key={tx.transaction_id} className="grid grid-cols-[150px_minmax(0,1fr)_80px_80px_80px_100px_minmax(0,1fr)] gap-2 items-center px-4 py-2 border-b border-slate-50 hover:bg-slate-50" data-testid={`tx-${tx.transaction_id}`}>
              <span className="text-xs text-slate-500">{tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</span>
              <span className="text-sm font-medium text-slate-800 truncate flex items-center gap-1"><TrendingUp className="h-3 w-3 text-emerald-500" /> {tx.ingredient_name || tx.ingredient_id}</span>
              <span className="text-right text-sm font-mono font-semibold text-emerald-600">+{tx.qty}</span>
              <span className="text-sm text-slate-500">{tx.unit}</span>
              <span className="text-right text-sm font-mono text-slate-600">${(tx.cost_per_unit || 0).toFixed(2)}</span>
              <span className="text-sm text-slate-500 truncate">{getVendorName(tx.vendor_id) || "—"}</span>
              <span className="text-xs text-slate-400 truncate">{tx.notes || "—"}</span>
            </div>
          ))}
          {transactions.filter((t) => t.transaction_type === "receive").length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No receiving records yet.</div>}
        </div>
      )}

      {/* Spoilage Log Tab */}
      {tab === "spoilage" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="spoilage-log">
          <div className="grid grid-cols-[150px_minmax(0,1fr)_80px_80px_80px_minmax(0,1fr)] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b">
            <div>Date</div><div>Ingredient</div><div className="text-right">Qty</div><div>Unit</div><div className="text-right">Est. Loss</div><div>Notes</div>
          </div>
          {transactions.filter((t) => t.transaction_type === "spoil").map((tx) => (
            <div key={tx.transaction_id} className="grid grid-cols-[150px_minmax(0,1fr)_80px_80px_80px_minmax(0,1fr)] gap-2 items-center px-4 py-2 border-b border-slate-50 hover:bg-slate-50" data-testid={`tx-${tx.transaction_id}`}>
              <span className="text-xs text-slate-500">{tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</span>
              <span className="text-sm font-medium text-slate-800 truncate flex items-center gap-1"><TrendingDown className="h-3 w-3 text-red-500" /> {tx.ingredient_name || tx.ingredient_id}</span>
              <span className="text-right text-sm font-mono font-semibold text-red-600">-{tx.qty}</span>
              <span className="text-sm text-slate-500">{tx.unit}</span>
              <span className="text-right text-sm font-mono text-red-600">${(tx.total_cost || 0).toFixed(2)}</span>
              <span className="text-xs text-slate-400 truncate">{tx.notes || "—"}</span>
            </div>
          ))}
          {transactions.filter((t) => t.transaction_type === "spoil").length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No spoilage records yet.</div>}
        </div>
      )}

      {/* Ingredient Dialog */}
      <Dialog open={showIngDialog} onOpenChange={setShowIngDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="ingredient-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingIng ? "Edit Ingredient" : "New Ingredient"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Track inventory levels for this ingredient</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="grid gap-4 py-2 px-1">
              <div>
                <Label className="text-xs text-slate-500">Name *</Label>
                <Input value={ingForm.name} onChange={(e) => setIngForm({ ...ingForm, name: e.target.value })} placeholder="e.g., Large Pizza Dough" data-testid="ing-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Stock Unit</Label>
                  <Select value={ingForm.stock_unit} onValueChange={(v) => setIngForm({ ...ingForm, stock_unit: v })}>
                    <SelectTrigger data-testid="ing-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Category</Label>
                  <Select value={ingForm.category || "none"} onValueChange={(v) => setIngForm({ ...ingForm, category: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Current Stock</Label>
                  <Input type="number" step="0.1" value={ingForm.current_stock} onChange={(e) => setIngForm({ ...ingForm, current_stock: e.target.value })} data-testid="ing-stock-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Reorder Level</Label>
                  <Input type="number" step="0.1" value={ingForm.reorder_level} onChange={(e) => setIngForm({ ...ingForm, reorder_level: e.target.value })} data-testid="ing-reorder-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Cost per Unit ($)</Label>
                  <Input type="number" step="0.01" value={ingForm.cost_per_unit} onChange={(e) => setIngForm({ ...ingForm, cost_per_unit: e.target.value })} data-testid="ing-cost-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Vendor</Label>
                  <Select value={ingForm.vendor_id || "none"} onValueChange={(v) => setIngForm({ ...ingForm, vendor_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Select vendor..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Case Size (units/case)</Label>
                  <Input type="number" step="1" value={ingForm.case_size} onChange={(e) => setIngForm({ ...ingForm, case_size: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={ingForm.is_active} onCheckedChange={(v) => setIngForm({ ...ingForm, is_active: v })} />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIngDialog(false)}>Cancel</Button>
            <Button onClick={saveIng} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="ing-save-btn">{editingIng ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive/Spoil Transaction Dialog */}
      <Dialog open={showTxDialog} onOpenChange={setShowTxDialog}>
        <DialogContent className="sm:max-w-md" data-testid="tx-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {txType === "receive" ? <><ArrowDownToLine className="h-5 w-5 text-emerald-600" /> Receive Inventory</> : <><Flame className="h-5 w-5 text-red-500" /> Log Spoilage</>}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {txType === "receive" ? "Record incoming inventory from a vendor" : "Record spoiled or wasted inventory"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Ingredient *</Label>
              <Select value={txForm.ingredient_id || "none"} onValueChange={(v) => {
                const ing = ingredients.find((i) => i.ingredient_id === v);
                setTxForm({ ...txForm, ingredient_id: v === "none" ? "" : v, unit: ing?.stock_unit || "", cost_per_unit: String(ing?.cost_per_unit || "") });
              }}>
                <SelectTrigger data-testid="tx-ingredient-select"><SelectValue placeholder="Select ingredient..." /></SelectTrigger>
                <SelectContent>{ingredients.map((i) => <SelectItem key={i.ingredient_id} value={i.ingredient_id}>{i.name} ({(i.current_stock || 0).toFixed(1)} {i.stock_unit})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Quantity *</Label>
                <Input type="number" step="0.1" value={txForm.qty} onChange={(e) => setTxForm({ ...txForm, qty: e.target.value })} data-testid="tx-qty-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Unit</Label>
                <Select value={txForm.unit || "none"} onValueChange={(v) => setTxForm({ ...txForm, unit: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Stock unit" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Default</SelectItem>
                    {UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {txType === "receive" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Cost per Unit ($)</Label>
                    <Input type="number" step="0.01" value={txForm.cost_per_unit} onChange={(e) => setTxForm({ ...txForm, cost_per_unit: e.target.value })} data-testid="tx-cost-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Vendor</Label>
                    <Select value={txForm.vendor_id || "none"} onValueChange={(v) => setTxForm({ ...txForm, vendor_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <Input value={txForm.notes} onChange={(e) => setTxForm({ ...txForm, notes: e.target.value })} placeholder={txType === "spoil" ? "Reason for spoilage..." : "PO #, delivery notes..."} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTxDialog(false)}>Cancel</Button>
            <Button
              onClick={saveTx}
              className={txType === "receive" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              data-testid="tx-save-btn"
            >
              {txType === "receive" ? "Receive Stock" : "Log Spoilage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
